import {
  TaxRateSchedule_Create,
  TaxRateSchedule_Read,
  TaxRateSchedule_Read_Nested,
  TaxRateSchedule_Read_Typeahead,
  TaxRateSchedule_Update,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { CompanyBasic } from '~hooks/useCompany/models';
import { Nullable } from '~types/Nullable';

export class TaxRateScheduleBasic {
  public static parse(proto: TaxRateSchedule_Read_Nested) {
    return new TaxRateScheduleBasic(
      proto.id,
      proto.name,
      proto.rate,
      proto.external_accounting_id,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static formatLabel(name: string, rate: Nullable<string>): string {
    return rate ? `${name} (${rate}%)` : name;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _rate: Nullable<string>,
    private _external_accounting_id: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get rate() {
    return this._rate;
  }

  public get externalAccountingId() {
    return this._external_accounting_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }
}

export class TaxRateScheduleTypeahead {
  public static parse(proto: TaxRateSchedule_Read_Typeahead) {
    return new TaxRateScheduleTypeahead(proto.id, proto.name, proto.rate);
  }

  constructor(
    private _id: string,
    private _name: string,
    private _rate: Nullable<string>,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get rate() {
    return this._rate;
  }
}

export class TaxRateSchedule {
  public static parse(proto: TaxRateSchedule_Read) {
    return new TaxRateSchedule(
      proto.id,
      proto.name,
      proto.rate,
      proto.external_accounting_id,
      proto.external_id,
      CompanyBasic.parse(proto.company),
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparseCreate(data: {
    name: string;
    rate?: number | null;
    externalAccountingId?: string | null;
    externalId?: string | null;
  }) {
    const result: TaxRateSchedule_Create = {
      name: data.name,
      rate: data.rate ?? null,
      external_accounting_id: data.externalAccountingId ?? null,
      external_id: data.externalId ?? null,
    };

    return result;
  }

  public static deparseUpdate(data: {
    name?: string;
    rate?: number | null;
    externalAccountingId?: string | null;
    externalId?: string | null;
  }) {
    const result: TaxRateSchedule_Update = {
      name: data.name,
      rate: data.rate ?? null,
      external_accounting_id: data.externalAccountingId ?? null,
      external_id: data.externalId ?? null,
    };

    return result;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _rate: Nullable<string>,
    private _external_accounting_id: Nullable<string>,
    private _external_id: Nullable<string>,
    private _company: CompanyBasic,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public toTypeahead() {
    return new TaxRateScheduleTypeahead(this._id, this._name, this._rate);
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get rate() {
    return this._rate;
  }

  public get externalAccountingId() {
    return this._external_accounting_id;
  }

  public get externalId() {
    return this._external_id;
  }

  public get company() {
    return this._company;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get discardedAt() {
    return this._discarded_at;
  }
}
